/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2022.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QInstance} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QInstance";
import {QProcessMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QProcessMetaData";
import {QReportMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QReportMetaData";
import ProcessFilterStorage from "qqq/utils/qqq/ProcessFilterStorage";

/*** Past this length, a filter goes in IndexedDB instead of the process's
 ** URL.  Servers reject request lines much longer than 8KB on reload, and
 ** analytics scripts scan the full URL on every hit, which has hung the browser
 ** for a minute on an 80,000-character bulk-edit URL. ***/
export const MAX_FILTER_JSON_URL_LENGTH = 2000;

/*******************************************************************************
 ** Utility class for working with QQQ Processes
 **
 *******************************************************************************/
class ProcessUtils
{
   public static getProcessesForTable(metaData: QInstance, tableName: string, includeHidden = false): QProcessMetaData[]
   {
      const matchingProcesses: QProcessMetaData[] = [];
      if (metaData.processes)
      {
         const processKeys = [...metaData.processes.keys()];
         processKeys.forEach((key) =>
         {
            const process = metaData.processes.get(key);
            if (process.tableName === tableName && (includeHidden || !process.isHidden))
            {
               matchingProcesses.push(process);
            }
         });
      }
      return matchingProcesses;
   }

   public static getReportsForTable(metaData: QInstance, tableName: string, includeHidden = false): QReportMetaData[]
   {
      const matchingReports: QReportMetaData[] = [];
      if (metaData.reports)
      {
         const reportKeys = [...metaData.reports.keys()];
         reportKeys.forEach((key) =>
         {
            const process = metaData.reports.get(key);
            if (process.tableName === tableName)
            {
               matchingReports.push(process);
            }
         });
      }
      return matchingReports;
   }


   /*******************************************************************************
    ** Build the query string that passes a filter to a process.  A filter that
    ** fits stays in the URL, so the link can be shared and reloaded.  A larger one
    ** gets a key in the URL, and is written to IndexedDB under that key in the
    ** background (see ProcessFilterStorage) - nothing waits for that write,
    ** because the launching screen also hands the process the filter in memory.
    ** The stored copy is only for a reload, or opening the URL in another tab of
    ** this browser; if the write fails, those show the missing-filter error.
    *******************************************************************************/
   public static buildFilterJSONQueryString(filterJSON: string): string
   {
      const encodedFilterJSON = encodeURIComponent(filterJSON);
      if (encodedFilterJSON.length <= MAX_FILTER_JSON_URL_LENGTH)
      {
         return `?recordsParam=filterJSON&filterJSON=${encodedFilterJSON}`;
      }

      const filterKey = ProcessFilterStorage.makeKey();
      ProcessFilterStorage.storeFilterJSONUnderKey(filterKey, filterJSON).catch((e) =>
      {
         console.log(`Could not store filter in IndexedDB - a reload of this process will not find it: ${e}`);
      });
      return `?recordsParam=filterJSON&filterKey=${encodeURIComponent(filterKey)}`;
   }


   /*******************************************************************************
    ** Get the filter stored by buildFilterJSONQueryString under a key - or null if
    ** this browser doesn't have it.
    *******************************************************************************/
   public static async getFilterJSONForKey(filterKey: string): Promise<string | null>
   {
      return (ProcessFilterStorage.getFilterJSON(filterKey));
   }

}

export default ProcessUtils;
